/* eslint-disable react-refresh/only-export-components */
import { Filter, Star, MessageCircle, RotateCcw, CheckCircle } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'
import { DateRangeFilter, type DateFilterValue } from './date-range-filter'

export interface ReviewFiltersState {
  rating: string
  sentiment: string
  /** A server preset, or `{preset: 'custom'}` carrying an explicit range. */
  date: DateFilterValue
  responseStatus: string
}

interface ReviewFiltersProps {
  filters: ReviewFiltersState
  ratingCounts?: Record<string, number>
  onFilterChange: (key: 'rating' | 'sentiment' | 'responseStatus', value: string) => void
  onDateChange: (value: DateFilterValue) => void
  onClearFilters: () => void
}

export const defaultFilters: ReviewFiltersState = {
  rating: 'all',
  sentiment: 'all',
  date: { preset: 'all' },
  responseStatus: 'all',
}

/** True when a filter is narrowing the result set. */
function isActive(value: ReviewFiltersState[keyof ReviewFiltersState]): boolean {
  return typeof value === 'string' ? value !== 'all' : value.preset !== 'all'
}

export function ReviewFilters({
  filters,
  ratingCounts = {},
  onFilterChange,
  onDateChange,
  onClearFilters,
}: ReviewFiltersProps) {
  const { t } = useTranslation()
  const activeFiltersCount = Object.values(filters).filter(isActive).length
  const totalRatings = Object.values(ratingCounts).reduce((acc, count) => acc + count, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{t('reviews.filters.title')}</span>
          {activeFiltersCount > 0 && (
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
              {t('reviews.filters.activeCount', { count: activeFiltersCount })}
            </Badge>
          )}
        </div>

        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-xs h-8 text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3 mr-1.5" />
            {t('reviews.filters.resetAll')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Select value={filters.rating} onValueChange={(value) => onFilterChange('rating', value)}>
          <SelectTrigger className="text-sm min-w-[120px]">
            <Star className="h-3.5 w-3.5 mr-2 shrink-0 text-muted-foreground" />
            <SelectValue placeholder={t('reviews.filters.rating')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('reviews.filters.allRatings')} {totalRatings > 0 ? `(${totalRatings})` : ''}
            </SelectItem>
            <SelectItem value="5">
              ⭐⭐⭐⭐⭐ {ratingCounts['5'] !== undefined ? `(${ratingCounts['5']})` : ''}
            </SelectItem>
            <SelectItem value="4">
              ⭐⭐⭐⭐ {ratingCounts['4'] !== undefined ? `(${ratingCounts['4']})` : ''}
            </SelectItem>
            <SelectItem value="3">
              ⭐⭐⭐ {ratingCounts['3'] !== undefined ? `(${ratingCounts['3']})` : ''}
            </SelectItem>
            <SelectItem value="2">
              ⭐⭐ {ratingCounts['2'] !== undefined ? `(${ratingCounts['2']})` : ''}
            </SelectItem>
            <SelectItem value="1">
              ⭐ {ratingCounts['1'] !== undefined ? `(${ratingCounts['1']})` : ''}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.sentiment}
          onValueChange={(value) => onFilterChange('sentiment', value)}
        >
          <SelectTrigger className="text-sm min-w-[130px]">
            <MessageCircle className="h-3.5 w-3.5 mr-2 shrink-0 text-muted-foreground" />
            <SelectValue placeholder={t('reviews.filters.sentiment')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('reviews.filters.allSentiment')}</SelectItem>
            <SelectItem value="positive">{t('reviews.sentiment.positive')}</SelectItem>
            <SelectItem value="neutral">{t('reviews.sentiment.neutral')}</SelectItem>
            <SelectItem value="negative">{t('reviews.sentiment.negative')}</SelectItem>
          </SelectContent>
        </Select>

        <DateRangeFilter value={filters.date} onChange={onDateChange} />

        <Select
          value={filters.responseStatus}
          onValueChange={(value) => onFilterChange('responseStatus', value)}
        >
          <SelectTrigger className="text-sm min-w-[150px]">
            <CheckCircle className="h-3.5 w-3.5 mr-2 shrink-0 text-muted-foreground" />
            <SelectValue placeholder={t('reviews.filters.response')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('reviews.filters.allReviews')}</SelectItem>
            <SelectItem value="pending">{t('reviews.filters.pendingResponse')}</SelectItem>
            <SelectItem value="responded">{t('reviews.filters.responded')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
